// ═══════════════════════════════════════════════════════════════════════════
// TraceFull v7.18.0 — Transparency Requests Page (Sequencing #13 / Bundle T)
//
// Tenant-side triage UI for v7.10.0 V-A consumer intake. Shows the queue of
// shopper-submitted "request more info on this product" rows, filtered by
// status, with a reply form on the per-row drawer. Role-gated at the
// backend (admin / super_admin) — page assumes it has been navigated to.
//
// State machine (T4 — mirrored in routes/transparency.ts):
//   pending → in_progress, responded, closed
//   in_progress → responded, closed
//   responded / closed → terminal
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { transparencyRequestsApi } from '../api/client';
import type { TransparencyRequest } from '../api/client';
import { T, SectionHeader, Card, Chip, ErrorBox, Skeleton } from '../components/ui';

type StatusFilter = '' | 'pending' | 'in_progress' | 'responded' | 'closed';

const STATUS_COLOR: Record<string, string> = {
  pending:     T.amber,
  in_progress: T.sapphire,
  responded:   T.sage,
  closed:      T.sub,
};

const STATUS_LABEL: Record<string, string> = {
  pending:     'Pending',
  in_progress: 'In progress',
  responded:   'Responded',
  closed:      'Closed',
};

export default function TransparencyRequestsPage() {
  const [requests, setRequests] = useState<TransparencyRequest[]>([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [err,      setErr]      = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [selected, setSelected] = useState<TransparencyRequest | null>(null);

  // Load list whenever the filter changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    const params = statusFilter ? { status: statusFilter, limit: 100 } : { limit: 100 };
    transparencyRequestsApi.list(params)
      .then(r => {
        if (cancelled) return;
        setRequests(r.requests);
        setTotal(r.total);
      })
      .catch(e => { if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter]);

  const onPatched = (updated: TransparencyRequest) => {
    setRequests(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    setSelected(updated);
  };

  return (
    <div>
      <SectionHeader
        title="Transparency Requests"
        sub="Shopper questions submitted from the consumer scan page. Reply to close the loop."
      />

      {/* Filter bar */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600 }}>
            Status:
          </span>
          {([
            ['',            'All'],
            ['pending',     'Pending'],
            ['in_progress', 'In progress'],
            ['responded',   'Responded'],
            ['closed',      'Closed'],
          ] as Array<[StatusFilter, string]>).map(([val, label]) => (
            <button
              key={val || 'all'}
              onClick={() => setStatusFilter(val)}
              style={{
                padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                cursor: 'pointer',
                background: statusFilter === val ? T.copper + '20' : 'transparent',
                color:      statusFilter === val ? T.copper : T.sub,
                border: `1px solid ${statusFilter === val ? T.copper + '60' : T.border}`,
              }}
            >{label}</button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: T.sub }}>
            {loading ? 'Loading…' : `${total} request${total === 1 ? '' : 's'}`}
          </span>
        </div>
      </Card>

      {err && <ErrorBox message={err} />}

      {/* Table */}
      <Card>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2].map(i => <Skeleton key={i} h={48} />)}
          </div>
        ) : requests.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: T.sub, fontSize: 13 }}>
            No transparency requests yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>Question</th>
                <th style={thStyle}>Submitted</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={tdStyle}>{r.product_name ?? <code style={{ fontSize: 10 }}>{r.product_id.slice(0, 8)}</code>}</td>
                  <td style={{ ...tdStyle, maxWidth: 360 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.question_text}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, color: T.sub }}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {r.requester_email
                      ? <span style={{ fontSize: 11 }}>{r.requester_email}</span>
                      : <span style={{ fontSize: 11, color: T.sub, fontStyle: 'italic' }}>(anonymous)</span>}
                  </td>
                  <td style={tdStyle}>
                    <Chip
                      label={STATUS_LABEL[r.status] || r.status}
                      color={STATUS_COLOR[r.status] || T.sub}
                      small
                    />
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => setSelected(r)}
                      style={{
                        padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        cursor: 'pointer', background: 'transparent',
                        color: T.copper, border: `1px solid ${T.copper}60`,
                      }}
                    >Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selected && (
        <ReplyDrawer
          request={selected}
          onClose={() => setSelected(null)}
          onPatched={onPatched}
        />
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 10,
  color: '#8a8178',
  textTransform: 'uppercase',
  letterSpacing: .8,
  fontWeight: 700,
};
const tdStyle: React.CSSProperties = {
  padding: '12px 12px',
  color: '#f0ebe4',
  verticalAlign: 'middle',
};

// ── Reply drawer ─────────────────────────────────────────────────────────────
// Modal overlay. Shows the full question, reply input, and status-transition
// buttons. T7 — disables Reply button when requester_email is null.
function ReplyDrawer({
  request, onClose, onPatched,
}: {
  request: TransparencyRequest;
  onClose: () => void;
  onPatched: (r: TransparencyRequest) => void;
}) {
  const [responseText, setResponseText] = useState<string>(request.response_text ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string>('');
  const [msg, setMsg] = useState<string>('');

  const isTerminal = request.status === 'responded' || request.status === 'closed';
  const allowedNext: Record<string, string[]> = {
    pending:     ['in_progress', 'responded', 'closed'],
    in_progress: ['responded', 'closed'],
    responded:   [],
    closed:      [],
  };
  const transitions = allowedNext[request.status] ?? [];

  const patch = async (status: string | undefined, withResponseText: boolean) => {
    setSaving(true); setErr(''); setMsg('');
    try {
      const body: { status?: string; response_text?: string } = {};
      if (status) body.status = status;
      if (withResponseText && responseText.trim()) body.response_text = responseText.trim();
      const updated = await transparencyRequestsApi.patch(request.id, body);
      onPatched(updated);
      setMsg(status === 'responded' ? 'Reply sent — consumer notified by email.' :
             status === 'closed'    ? 'Request closed.' :
             status === 'in_progress' ? 'Marked as in progress.' :
             'Saved.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: '#0a09088c', backdropFilter: 'blur(4px)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 540, maxWidth: '95vw', height: '100vh', overflowY: 'auto',
          background: T.bg, borderLeft: `1px solid ${T.border}`,
          padding: '28px 30px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>
              {request.product_name ?? <code>{request.product_id.slice(0, 8)}</code>}
            </div>
            <Chip
              label={STATUS_LABEL[request.status] || request.status}
              color={STATUS_COLOR[request.status] || T.sub}
              small
            />
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: `1px solid ${T.border}`,
              color: T.sub, borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 12,
            }}
          >Close</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600, marginBottom: 6 }}>
            Question
          </div>
          <div style={{
            background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
            padding: 14, fontSize: 13, color: T.text, lineHeight: 1.6, whiteSpace: 'pre-wrap',
          }}>
            {request.question_text}
          </div>
        </div>

        <div style={{ marginBottom: 16, display: 'flex', gap: 16, fontSize: 11, color: T.sub }}>
          <span>From: {request.requester_email ?? <em>(anonymous)</em>}</span>
          <span>Submitted: {new Date(request.created_at).toLocaleString()}</span>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, fontWeight: 600, marginBottom: 6 }}>
            Response
          </div>
          {isTerminal && request.response_text ? (
            <div style={{
              background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: 14, fontSize: 13, color: T.text, lineHeight: 1.6, whiteSpace: 'pre-wrap',
            }}>
              {request.response_text}
              {request.responder_name && (
                <div style={{ fontSize: 10, color: T.sub, marginTop: 10 }}>
                  — {request.responder_name}{request.responded_at && `, ${new Date(request.responded_at).toLocaleString()}`}
                </div>
              )}
            </div>
          ) : isTerminal ? (
            <div style={{ fontSize: 12, color: T.sub, fontStyle: 'italic' }}>
              (no response — request closed without reply)
            </div>
          ) : (
            <textarea
              value={responseText}
              onChange={e => setResponseText(e.target.value)}
              placeholder={request.requester_email
                ? 'Write your response here. The consumer will receive it by email.'
                : '(no email on file — you can only close this request)'}
              disabled={saving || !request.requester_email}
              rows={6}
              style={{
                width: '100%', background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
                padding: 12, color: T.text, fontSize: 13, lineHeight: 1.6,
                fontFamily: 'inherit', resize: 'vertical', outline: 'none',
              }}
            />
          )}
        </div>

        {msg && (
          <div style={{
            background: `${T.sage}15`, border: `1px solid ${T.sage}40`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: T.sage,
          }}>
            {msg}
          </div>
        )}
        {err && (
          <div style={{
            background: `${T.crimson}10`, border: `1px solid ${T.crimson}40`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: T.crimson,
          }}>
            {err}
          </div>
        )}

        {/* Action buttons */}
        {!isTerminal && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {transitions.includes('in_progress') && (
              <button
                onClick={() => patch('in_progress', false)}
                disabled={saving}
                style={btn(T.sapphire, saving)}
              >Mark in progress</button>
            )}
            {transitions.includes('responded') && (
              <button
                onClick={() => patch('responded', true)}
                disabled={saving || !request.requester_email || !responseText.trim()}
                style={btn(T.sage, saving || !request.requester_email || !responseText.trim())}
              >Send reply & mark responded</button>
            )}
            {transitions.includes('closed') && (
              <button
                onClick={() => patch('closed', false)}
                disabled={saving}
                style={btn(T.sub, saving)}
              >Close without reply</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function btn(color: string, disabled: boolean): React.CSSProperties {
  return {
    padding: '9px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? T.border : color,
    color: disabled ? T.sub : '#fff',
    border: `1px solid ${disabled ? T.border : color}`,
    opacity: disabled ? 0.6 : 1,
  };
}
